/**
 * Header badges for the headline markets (issue #413): joins `/fear-greed/markets`
 * quotes with the ONE symbol list in src/shared/headlineMarkets.js, in that list's
 * order. A symbol missing from the payload, or carrying no finite move, is left out
 * rather than shown as 0.00% -- absence is not "flat".
 */
import { HEADLINE_MARKETS } from '../shared/headlineMarkets';

const finite = (v) => typeof v === 'number' && Number.isFinite(v);
const MINUS = '−';

// Shared by signedPercent and the raw-change formatting below: '+'/MINUS/'' ahead of the
// magnitude, so the two never drift on how a positive/negative/zero value is signed.
const signPrefix = (v) => (v > 0 ? '+' : v < 0 ? MINUS : '');

export const signedPercent = (pct) => `${signPrefix(pct)}${Math.abs(pct).toFixed(2)}%`;

const level = (v) => v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const asOfLabel = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    timeZone: 'America/New_York', weekday: 'short', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  });
};

export function headlineMarketBadges(payload) {
  const bySymbol = new Map((payload?.markets || []).map((m) => [m.symbol, m]));
  return HEADLINE_MARKETS.flatMap(({ symbol, label, name }) => {
    const quote = bySymbol.get(symbol);
    if (!quote || !finite(quote.changePercent) || !finite(quote.price) || !finite(quote.change)) return [];
    const when = asOfLabel(quote.asOf);
    const change = `${signPrefix(quote.change)}${level(Math.abs(quote.change))}`;
    return [{
      symbol,
      text: `${label} ${signedPercent(quote.changePercent)}`,
      tone: quote.changePercent > 0 ? 'positive' : quote.changePercent < 0 ? 'negative' : 'info',
      title: `${name} ${level(quote.price)} · ${change} (${signedPercent(quote.changePercent)}) vs previous close${when ? ` · as of ${when}` : ''}`,
    }];
  });
}
